"use client";

import React, { useMemo, useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { X, Banknote } from "lucide-react";
import PaymentCompleteModal from "./PaymentCompleteModal";
import styles from "./css/codRefundItems.module.css";
import { getReceiptDetails as apiGetReceiptDetails } from "../../../api/frontShop/ReceiptAPI";
import { processRefund as apiProcessRefund } from "../../../api/frontShop/RefundsAPI";
import PrintReceiptDialog from "../../../components/print/views/PrintReceiptDialog";
import RefundAuthPinDialog from "./RefundAuthPinDialog";

export default function CodRefundItems() {
  const location = useLocation();
  const navigate = useNavigate();
  const recieptno = location.state?.recieptno;
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [items, setItems] = useState([]);
  const [refundAll, setRefundAll] = useState(false);
  const [showSummary, setShowSummary] = useState(false);
  const [showRefundComplete, setShowRefundComplete] = useState(false);
  const [printOpen, setPrintOpen] = useState(false);
  const [pinOpen, setPinOpen] = useState(false);
  const [purchaseAmount, setPurchaseAmount] = useState(0);
  const [refundReason, setRefundReason] = useState("");
  const [otherRefundReason, setOtherRefundReason] = useState("");

  const totalRefundAmount = useMemo(() => {
    const subtotal = items.reduce((sum, item) => sum + (item.checked ? item.qty * (Number(item.price) || 0) : 0), 0);
    return subtotal * 1.15; // include VAT 15%
  }, [items]);

  const selectedItems = useMemo(() => items.filter((item) => item.checked && item.qty > 0), [items]);

  const resolvedRefundReason = useMemo(() => {
    if (refundReason === "Other") return String(otherRefundReason || "").trim();
    return String(refundReason || "").trim();
  }, [refundReason, otherRefundReason]);

  const handleToggleRefundAll = () => {
    setRefundAll((prev) => {
      const next = !prev;
      setItems((current) =>
        current.map((item) => (next ? { ...item, checked: true, qty: item.maxQty } : { ...item, checked: false, qty: 0 }))
      );
      return next;
    });
  };

  const handleToggleItem = (id) => {
    setItems((current) => {
      const updated = current.map((item) =>
        item.id === id
          ? {
              ...item,
              checked: !item.checked,
              qty: !item.checked ? (item.qty === 0 ? item.maxQty : item.qty) : 0,
            }
          : item
      );
      const allCheckedAndMax = updated.every((item) => item.checked && item.qty === item.maxQty);
      setRefundAll(allCheckedAndMax);
      return updated;
    });
  };

  const updateQty = (id, delta) => {
    setItems((current) => {
      const updated = current.map((item) => {
        if (item.id !== id) return item;
        const nextQty = Math.max(0, Math.min(item.maxQty, item.qty + delta));
        return { ...item, qty: nextQty, checked: nextQty > 0 };
      });
      const allCheckedAndMax = updated.every((item) => item.checked && item.qty === item.maxQty);
      setRefundAll(allCheckedAndMax);
      return updated;
    });
  };

  const fromPath = "/frontshop/orders";

  const handleClose = () => {
    navigate(fromPath, { state: location.state });
  };

  const handleContinue = () => {
    if (!selectedItems.length) return;
    if (!resolvedRefundReason) {
      setError("Please select a refund reason");
      return;
    }
    setShowSummary(true);
  };

  const doProcessRefund = async (authcode) => {
    if (!recieptno) return { ok: false, message: "Missing receipt number" };
    try {
      const refunds = items
        .filter((i) => i.checked && i.qty > 0)
        .map((i) => ({ sku: i.id, qty: i.qty }));

      const { data } = await apiProcessRefund({ recieptno, refunds, refundreason: resolvedRefundReason, authcode });
      if (data?.success) {
        setShowSummary(false);
        setPrintOpen(true);
        return { ok: true };
      }
      setError(data?.message || "Failed to process refund");
      return { ok: false, message: data?.message || "Not authorized" };
    } catch (e) {
      const serverMsg = e?.response?.data?.message || e?.response?.data?.error;
      const msg = serverMsg || e?.message || "Not authorized";
      setError(msg || "Failed to process refund");
      return { ok: false, message: msg || "Not authorized" };
    }
  };

  const handleProcessRefund = () => {
    if (!recieptno) return;
    setError("");
    setPinOpen(true);
  };

  useEffect(() => {
    let cancelled = false;
    async function load() {
      if (!recieptno) return;
      setLoading(true);
      setError("");
      try {
        const { data: json } = await apiGetReceiptDetails(recieptno);
        const receipt = json?.data;
        const srcItems = Array.isArray(receipt?.items) ? receipt.items : [];
        // srcItems: { sku, name, qty, unitPrice }
        const mapped = srcItems.map((it) => ({
          id: it.sku,
          name: `${it.name} x${Number(it.qty || 0)}`,
          price: Number(it.unitPrice || 0), // per-unit costprice
          maxQty: Number(it.qty || 0),
          checked: false,
          qty: 0,
        }));
        if (!cancelled) {
          setItems(mapped);
          setPurchaseAmount(Number(receipt?.totals?.totalamount || 0));
        }
      } catch (e) {
        if (!cancelled) setError(e?.message || "Failed to load receipt items");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [recieptno]);

  return (
    <div className={styles.overlay}>
      <div className={styles.modal}>
        
        <PrintReceiptDialog
          open={printOpen}
          recieptno={recieptno}
          onClose={() => {
            setPrintOpen(false);
            setShowRefundComplete(true);
          }}
        />
        <button type="button" className={styles.closeButton} onClick={handleClose} aria-label="Close">
          <X className={styles.closeIcon} />
        </button>
        <div className={styles.title}>
          {showSummary ? "Items to be refunded:" : "Select items you want to refund:"}
        </div>

        {!showSummary && (
          <>
            <div className={styles.contentRow}>
              <div className={styles.tableCard}>
                <div className={styles.tableHeaderRow}>
                  <div>Item Name</div>
                  <div>Cost Price</div>
                  <div className={styles.checkHeader}>Check</div>
                </div>
                <div className={styles.tableBody}>
                  {loading && <div className={styles.cellItemName}>Loading...</div>}
                  {error && <div className={styles.cellItemName}>{error}</div>}
                  {!loading && !error && items.map((item) => (
                    <div key={item.id} className={styles.tableBodyRow}>
                      <div className={styles.cellItemName}>{item.name}</div>
                      <div className={styles.cellPrice}>R {(Number(item.price || 0) * Number(item.maxQty || 0)).toFixed(2)}</div>
                      <div className={styles.checkboxCell}>
                        <input
                          type="checkbox"
                          className={styles.checkbox}
                          checked={item.checked}
                          onChange={() => handleToggleItem(item.id)}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div className={styles.sideControls}>
                <div className={styles.refundAllRow}>
                  <input
                    type="checkbox"
                    className={styles.checkbox}
                    checked={refundAll}
                    onChange={handleToggleRefundAll}
                  />
                  <span className={styles.refundAllLabel}>Refund All</span>
                </div>
                {!refundAll &&
                  items.map((item) => (
                    <div key={item.id} className={styles.quantityControl}>
                      <button
                        type="button"
                        className={styles.quantityButton}
                        onClick={() => updateQty(item.id, -1)}
                        disabled={item.qty <= 0}
                      >
                        -
                      </button>
                      <div className={styles.quantityValueBox}>{item.qty}</div>
                      <button
                        type="button"
                        className={styles.quantityButton}
                        onClick={() => updateQty(item.id, 1)}
                        disabled={item.qty >= item.maxQty}
                      >
                        +
                      </button>
                    </div>
                  ))}
              </div>
            </div>
            <div className={styles.refundReasonRow}>
              <div className={styles.refundReasonLabel}>Refund Reason</div>
              <select
                className={styles.refundReasonSelect}
                value={refundReason}
                onChange={(e) => {
                  const v = e.target.value;
                  setRefundReason(v);
                  setError("");
                  if (v !== "Other") setOtherRefundReason("");
                }}
              >
                <option value="" disabled>
                  Select a reason...
                </option>
                <option value="Adverse reaction reported after purchase.">Adverse reaction reported after purchase.</option>
                <option value="Product damaged or leaking.">Product damaged or leaking.</option>
                <option value="Product expired or near expiry at purchase.">Product expired or near expiry at purchase.</option>
                <option value="Billing or pricing error.">Billing or pricing error.</option>
                <option value="Item recalled by manufacturer.">Item recalled by manufacturer.</option>
                <option value="Other">Other</option>
              </select>

              {refundReason === "Other" && (
                <input
                  type="text"
                  className={styles.refundReasonOtherInput}
                  value={otherRefundReason}
                  onChange={(e) => {
                    setOtherRefundReason(e.target.value);
                    setError("");
                  }}
                  placeholder="Enter refund reason"
                />
              )}
            </div>

            <div className={styles.footerRow}>
              <div className={styles.totalLabel}>
                Total Amount to be Refunded: {" "}
                <span className={styles.totalAmount}>R {totalRefundAmount.toFixed(2)}</span>
              </div>
              <button type="button" className={styles.continueButton} onClick={handleContinue}>
                Continue
              </button>
            </div>
          </>
        )}

        {showSummary && (
          <>
            <div className={styles.summaryWrapper}>
              <div className={styles.summaryTableCard}>
                <div className={styles.summaryHeaderRow}>
                  <div>Item Name</div>
                  <div>Cost Price</div>
                </div>
                <div className={styles.summaryBody}>
                  {selectedItems.map((item) => (
                    <div key={item.id} className={styles.summaryBodyRow}>
                      <div className={styles.cellItemName}>{item.name}</div>
                      <div className={styles.cellPrice}>R {(Number(item.price || 0) * Number(item.qty || 0)).toFixed(2)}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className={styles.summaryReasonRow}>
              <div className={styles.summaryReasonCard}>
                <div className={styles.summaryReasonLabel}>Refund Reason</div>
                <div className={styles.summaryReasonValue}>{resolvedRefundReason || "-"}</div>
              </div>
            </div>

            <div className={styles.footerRow}>
              <div className={styles.totalLabel}>
                Amount Due: {" "}
                <span className={styles.totalAmount}>R {totalRefundAmount.toFixed(2)}</span>
              </div>
              <button type="button" className={styles.processButton} onClick={handleProcessRefund} disabled={pinOpen}>
                <Banknote className={styles.processIcon} />
                Process Refund
              </button>
            </div>

            {pinOpen ? (
              <div className={styles.authInlineRow}>
                <RefundAuthPinDialog
                  open={pinOpen}
                  inline
                  onClose={() => setPinOpen(false)}
                  onSubmit={async (code) => {
                    const result = await doProcessRefund(code);
                    if (!result?.ok) return result;
                    setPinOpen(false);
                    return result;
                  }}
                />
              </div>
            ) : null}
          </>
        )}

        {showRefundComplete && (
          <PaymentCompleteModal
            amountPaid={totalRefundAmount}
            paymentMethod={location.state?.paymentMethod || "Cash"}
            discountApplied={0}
            onNewSale={() => navigate("/frontshop/orders")}
            onClose={() => navigate("/frontshop/orders")}
            title="Refund Complete!"
            subtitle="Refund processed successfully"
            amountLabel="Amount Refunded"
            methodLabel="Refund Method"
            discountLabel="Discount Applied"
            primaryLabel="Close"
            showDiscount={false}
          />
        )}
      </div>
    </div>
  );
}
